"use client";

import {
  DEFAULT_PROMOTIONS,
  DEFAULT_RESERVATIONS,
  DEFAULT_ROOMS,
  DEFAULT_SETTINGS,
  DEFAULT_TARIFFS
} from "./defaults.mjs";
import { calculateScenarios } from "./calculator.mjs";

const API_URL = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
const DB_KEY = "felix-rate-desk-db-v1";
const SESSION_KEY = "felix-rate-desk-session-v1";
const TOKEN_KEY = "felix-rate-desk-token-v1";

const clone = (value) => JSON.parse(JSON.stringify(value));
const now = () => new Date().toISOString();
const uid = (prefix) =>
  `${prefix}-${globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`;

function seedDatabase() {
  return {
    settings: clone(DEFAULT_SETTINGS),
    rooms: clone(DEFAULT_ROOMS),
    reservations: clone(DEFAULT_RESERVATIONS),
    tariffs: clone(DEFAULT_TARIFFS),
    promotions: clone(DEFAULT_PROMOTIONS),
    users: [],
    activity: []
  };
}

function readDb() {
  if (typeof window === "undefined") return seedDatabase();
  const raw = window.localStorage.getItem(DB_KEY);
  if (!raw) {
    const seeded = seedDatabase();
    window.localStorage.setItem(DB_KEY, JSON.stringify(seeded));
    return seeded;
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed.settings?.calculatorMode !== "clientPrice") {
      const legacySettings = { ...DEFAULT_SETTINGS, ...(parsed.settings || {}) };
      const convertLegacyTarget = (value) =>
        calculateScenarios({
          targetNet: Number(value || 0),
          nights: 1,
          accommodationVatRate: legacySettings.accommodationVatRate,
          touristTaxRate: legacySettings.touristTaxRate,
          commissionRate: legacySettings.commissionRate,
          commissionVatRate: legacySettings.commissionVatRate,
          commissionVatRecoverable:
            legacySettings.commissionVatRecoverable,
          commissionBasis: legacySettings.commissionBasis,
          stackingMode: "best",
          promotions: []
        }).normal.discountedGross;
      parsed.rooms = (parsed.rooms || []).map((room) => ({
        ...room,
        targetNet: Number(convertLegacyTarget(room.targetNet).toFixed(2))
      }));
      parsed.tariffs = (parsed.tariffs || []).map((tariff) => ({
        ...tariff,
        targetNet: Number(convertLegacyTarget(tariff.targetNet).toFixed(2))
      }));
      parsed.settings = {
        ...(parsed.settings || {}),
        calculatorMode: "clientPrice"
      };
      window.localStorage.setItem(DB_KEY, JSON.stringify(parsed));
    }
    if (parsed.settings?.promotionBehaviorVersion !== 2) {
      parsed.settings = {
        ...(parsed.settings || {}),
        stackingMode: "stack",
        promotionBehaviorVersion: 2
      };
      window.localStorage.setItem(DB_KEY, JSON.stringify(parsed));
    }
    return {
      ...seedDatabase(),
      ...parsed,
      settings: { ...DEFAULT_SETTINGS, ...(parsed.settings || {}) }
    };
  } catch {
    return seedDatabase();
  }
}

function writeDb(db) {
  window.localStorage.setItem(DB_KEY, JSON.stringify(db));
}

function publicUser(user) {
  if (!user) return null;
  const { passwordHash, passwordSalt, ...safe } = user;
  return safe;
}

async function hashPassword(password, saltBase64) {
  const encoder = new TextEncoder();
  const salt = saltBase64
    ? Uint8Array.from(atob(saltBase64), (char) => char.charCodeAt(0))
    : crypto.getRandomValues(new Uint8Array(16));
  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(password),
    "PBKDF2",
    false,
    ["deriveBits"]
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      salt,
      iterations: 180000
    },
    key,
    256
  );
  const hash = btoa(String.fromCharCode(...new Uint8Array(bits)));
  const encodedSalt = btoa(String.fromCharCode(...salt));
  return { hash, salt: encodedSalt };
}

function currentLocalUser(db = readDb()) {
  const userId = window.localStorage.getItem(SESSION_KEY);
  return publicUser(
    db.users.find((user) => user.id === userId && user.active !== false)
  );
}

function logActivity(db, action, detail, user = currentLocalUser(db)) {
  db.activity.unshift({
    id: uid("event"),
    at: now(),
    action,
    detail,
    userName: user?.name || "System"
  });
  db.activity = db.activity.slice(0, 100);
}

function validateReservation(db, value, existingId = null) {
  if (!value.roomId || !value.checkIn || !value.checkOut) {
    throw new Error("Room, check-in and check-out are required.");
  }
  if (value.checkOut <= value.checkIn) {
    throw new Error("Check-out must be after check-in.");
  }
  const blockingStatuses = new Set(["pending", "confirmed", "checked_in"]);
  if (!blockingStatuses.has(value.status || "confirmed")) return;
  const conflict = db.reservations.find(
    (reservation) =>
      reservation.id !== existingId &&
      reservation.roomId === value.roomId &&
      blockingStatuses.has(reservation.status) &&
      value.checkIn < reservation.checkOut &&
      value.checkOut > reservation.checkIn
  );
  if (conflict) {
    throw new Error(
      `This room is already reserved from ${conflict.checkIn} to ${conflict.checkOut}.`
    );
  }
}

async function remoteRequest(path, options = {}) {
  const token = window.localStorage.getItem(TOKEN_KEY);
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    }
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || "Request failed.");
  }
  if (payload.token) window.localStorage.setItem(TOKEN_KEY, payload.token);
  return payload;
}

const localApi = {
  mode: "device",
  async status() {
    const db = readDb();
    return { needsSetup: db.users.length === 0, mode: "device" };
  },
  async bootstrap(value) {
    const db = readDb();
    if (db.users.length) throw new Error("The owner account already exists.");
    const secured = await hashPassword(value.password);
    const user = {
      id: uid("user"),
      name: value.name.trim(),
      email: value.email.trim().toLowerCase(),
      role: "admin",
      active: true,
      passwordHash: secured.hash,
      passwordSalt: secured.salt,
      createdAt: now(),
      updatedAt: now()
    };
    db.users.push(user);
    logActivity(db, "setup", "Created the property owner account", user);
    writeDb(db);
    window.localStorage.setItem(SESSION_KEY, user.id);
    return { user: publicUser(user) };
  },
  async login(value) {
    const db = readDb();
    const user = db.users.find(
      (candidate) =>
        candidate.email === value.email.trim().toLowerCase() &&
        candidate.active !== false
    );
    if (!user) throw new Error("Incorrect email or password.");
    const secured = await hashPassword(value.password, user.passwordSalt);
    if (secured.hash !== user.passwordHash) {
      throw new Error("Incorrect email or password.");
    }
    window.localStorage.setItem(SESSION_KEY, user.id);
    return { user: publicUser(user) };
  },
  async me() {
    return { user: currentLocalUser() };
  },
  async logout() {
    window.localStorage.removeItem(SESSION_KEY);
    return { ok: true };
  },
  async snapshot() {
    const db = readDb();
    return {
      settings: clone(db.settings),
      rooms: clone(db.rooms),
      reservations: clone(db.reservations),
      tariffs: clone(db.tariffs),
      promotions: clone(db.promotions),
      users: db.users.map(publicUser),
      activity: clone(db.activity),
      mode: "device"
    };
  },
  async updateSettings(value) {
    const db = readDb();
    db.settings = { ...db.settings, ...value, updatedAt: now() };
    logActivity(db, "settings.updated", "Updated pricing and property settings");
    writeDb(db);
    return clone(db.settings);
  },
  async create(resource, value) {
    const db = readDb();
    if (!Array.isArray(db[resource])) throw new Error("Unknown resource.");
    if (resource === "reservations") validateReservation(db, value);
    if (resource === "users") {
      const actor = currentLocalUser(db);
      if (actor?.role !== "admin") throw new Error("Administrator access required.");
      if (
        db.users.some(
          (user) => user.email === value.email.trim().toLowerCase()
        )
      ) {
        throw new Error("A user with this email already exists.");
      }
      const secured = await hashPassword(value.password);
      value = {
        ...value,
        email: value.email.trim().toLowerCase(),
        passwordHash: secured.hash,
        passwordSalt: secured.salt
      };
      delete value.password;
    }
    const singular = resource.endsWith("s")
      ? resource.slice(0, -1)
      : resource;
    const record = {
      ...value,
      id: uid(singular),
      createdAt: now(),
      updatedAt: now()
    };
    db[resource].push(record);
    logActivity(db, `${singular}.created`, `Created ${record.name || record.guestName || singular}`);
    writeDb(db);
    return publicUser(record);
  },
  async update(resource, id, value) {
    const db = readDb();
    const index = db[resource]?.findIndex((record) => record.id === id);
    if (index == null || index < 0) throw new Error("Record not found.");
    if (resource === "reservations") {
      validateReservation(db, { ...db[resource][index], ...value }, id);
    }
    if (resource === "users") {
      const actor = currentLocalUser(db);
      if (actor?.role !== "admin") throw new Error("Administrator access required.");
      if (value.password) {
        const secured = await hashPassword(value.password);
        value.passwordHash = secured.hash;
        value.passwordSalt = secured.salt;
      }
      delete value.password;
      if (value.email) value.email = value.email.trim().toLowerCase();
    }
    const record = {
      ...db[resource][index],
      ...value,
      id,
      updatedAt: now()
    };
    db[resource][index] = record;
    logActivity(
      db,
      `${resource}.updated`,
      `Updated ${record.name || record.guestName || resource}`
    );
    writeDb(db);
    return publicUser(record);
  },
  async remove(resource, id) {
    const db = readDb();
    const index = db[resource]?.findIndex((record) => record.id === id);
    if (index == null || index < 0) throw new Error("Record not found.");
    const [record] = db[resource].splice(index, 1);
    logActivity(
      db,
      `${resource}.deleted`,
      `Deleted ${record.name || record.guestName || resource}`
    );
    writeDb(db);
    return { ok: true };
  },
  async exportData() {
    const db = readDb();
    return {
      version: 1,
      exportedAt: now(),
      data: {
        ...db,
        users: db.users.map(publicUser)
      }
    };
  }
};

const remoteApi = {
  mode: "cloud",
  status: () => remoteRequest("/status"),
  bootstrap: (value) =>
    remoteRequest("/auth/bootstrap", {
      method: "POST",
      body: JSON.stringify(value)
    }),
  login: (value) =>
    remoteRequest("/auth/login", {
      method: "POST",
      body: JSON.stringify(value)
    }),
  me: () => remoteRequest("/auth/me"),
  logout: async () => {
    window.localStorage.removeItem(TOKEN_KEY);
    return { ok: true };
  },
  snapshot: () => remoteRequest("/snapshot"),
  updateSettings: (value) =>
    remoteRequest("/settings", {
      method: "PUT",
      body: JSON.stringify(value)
    }),
  create: (resource, value) =>
    remoteRequest(`/${resource}`, {
      method: "POST",
      body: JSON.stringify(value)
    }),
  update: (resource, id, value) =>
    remoteRequest(`/${resource}/${id}`, {
      method: "PUT",
      body: JSON.stringify(value)
    }),
  remove: (resource, id) =>
    remoteRequest(`/${resource}/${id}`, { method: "DELETE" }),
  exportData: () => remoteRequest("/export")
};

export const api = API_URL ? remoteApi : localApi;
